import React from "react";
import { Link } from "react-router-dom";
import Header from "./Header";
import Footer from "./Footer";
import "../css/about.scss";

const toTop = () => {
  window.scrollTo({ top: 0, behavior: "smooth" });
};

const PILLARS = [
  {
    id: "respun",
    label: "The Re-Spun Program",
    copy: "Discover our approach to longer-lasting footwear and considered materials.",
    iconBg: "#d9e8e8",
    iconSrc: "/main/about-circularity.svg",
    linkTo: "/about",
    linkLabel: "Learn more",
    reverse: false,
  },
  {
    id: "story",
    label: "Our Story",
    copy: "Learn why we focus on versatile shoes for everyday movement.",
    iconBg: "#f3ede4",
    iconSrc: "/main/about-story.svg",
    linkTo: "/about",
    linkLabel: "Read on",
    reverse: true,
  },
  {
    id: "bcert",
    label: "Considered Materials",
    copy: "Making shoes with durable, thoughtfully selected materials.",
    iconBg: "#e4ede0",
    iconSrc: "/main/about-materials.svg",
    linkTo: "/shop",
    linkLabel: "See why",
    reverse: false,
  },
  {
    id: "impact",
    label: "Impact Report",
    copy: "The details behind our footwear, packaging, and delivery choices.",
    iconBg: "#f0e8e4",
    iconSrc: "/main/about-impact.svg",
    linkTo: "/shop",
    linkLabel: "Dig in",
    reverse: true,
  },
];

const STATS = [
  { value: "9", label: "Footwear styles in the current collection" },
  { value: "35–46", label: "Size range across selected styles" },
  { value: "3", label: "Product views for every pair" },
  { value: "1", label: "Dedicated footwear collection" },
];

const VALUES = [
  {
    title: "Made to Last",
    body: "We select shoes with versatile silhouettes, supportive construction, and materials chosen for repeat wear.",
  },
  {
    title: "People First",
    body: "Every person in our supply chain deserves fair wages and safe conditions. We audit partners regularly and publish the results.",
  },
  {
    title: "Comfort in Motion",
    body: "From flexible sneakers to stable heels, each style is selected with everyday movement in mind.",
  },
  {
    title: "Radical Transparency",
    body: "We clearly list materials, fit guidance, sole construction, and origin information on every product page.",
  },
];

function About() {
  return (
    <div className="about">
      <Header />

      <main className="about__main" id="MainContent">
        <section className="about__hero" aria-label="About mvandora">
          <div className="about__hero-bg">
            <img
              src="/img/editorial-shoes/about-community.png"
              alt=""
              aria-hidden="true"
              className="about__hero-img"
            />
            <div className="about__hero-overlay" />
          </div>
          <div className="about__hero-content">
            <h1 className="about__hero-title">About</h1>
            <p className="about__hero-sub">
              We focus on shoes that balance distinctive style with everyday
              comfort.
            </p>
          </div>
        </section>

        <section className="about__pillars" aria-label="What we stand for">
          <div className="about__pillars-inner">
            {PILLARS.map((p) => (
              <div
                key={p.id}
                className={`about__pillar ${
                  p.reverse ? "about__pillar--reverse" : ""
                }`}
              >
                <div
                  className="about__pillar-icon-wrap"
                  style={{ background: p.iconBg }}
                >
                  <img
                    src={p.iconSrc}
                    alt=""
                    aria-hidden="true"
                    className="about__pillar-icon"
                  />
                </div>
                <div className="about__pillar-body">
                  <h2 className="about__pillar-title">{p.label}</h2>
                  <p className="about__pillar-copy">{p.copy}</p>
                  <Link
                    className="about__pillar-link"
                    to={p.linkTo}
                    onClick={toTop}
                  >
                    {p.linkLabel}
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="about__stats" aria-labelledby="stats-heading">
          <h2 id="stats-heading" className="about__stats-heading">
            By the numbers
          </h2>
          <div className="about__stats-grid">
            {STATS.map((s) => (
              <div key={s.label} className="about__stat">
                <span className="about__stat-value">{s.value}</span>
                {s.unit ? (
                  <span className="about__stat-unit">{s.unit}</span>
                ) : null}
                <p className="about__stat-label">{s.label}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="about__values" aria-labelledby="values-heading">
          <div className="about__values-inner">
            <h2 id="values-heading" className="about__values-title">
              What drives us
            </h2>
            <div className="about__values-grid">
              {VALUES.map((v) => (
                <div key={v.title} className="about__value-card">
                  <h3 className="about__value-name">{v.title}</h3>
                  <p className="about__value-body">{v.body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="about__bcorp" aria-label="B Corp certification">
          <div className="about__bcorp-inner">
            <div className="about__bcorp-badge" aria-hidden="true">
              <img
                src="/main/about-footwear.svg"
                alt=""
                aria-hidden="true"
                className="about__bcorp-icon"
              />
            </div>
            <h2 className="about__bcorp-title">A Footwear-First Collection</h2>
            <p className="about__bcorp-body">
              Our collection brings together sneakers, loafers, Derby shoes,
              heels, and boots so every visit stays focused on footwear.
            </p>
            <p className="about__bcorp-body">
              Clear product details and practical fit notes make it easier to
              choose the right pair for work, weekends, and everything between.
            </p>
          </div>
        </section>

        <section className="about__cta" aria-label="Contact">
          <div className="about__cta-inner">
            <h2 className="about__cta-title">
              Let's keep the conversation going.
            </h2>
            <p className="about__cta-sub">
              Questions, ideas, or just want to say hey? We're all ears.
            </p>
            <div className="about__cta-actions">
              <Link
                className="about__cta-btn about__cta-btn--primary"
                to="/contact"
                onClick={toTop}
              >
                Get in touch
              </Link>
              <Link
                className="about__cta-btn about__cta-btn--outline"
                to="/shop"
                onClick={toTop}
              >
                Shop our collection
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}

export default About;
